import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { saasMySubscription } from '../api';
import { Clock, X, ArrowRight } from 'lucide-react';

const DISMISS_KEY = 'iacc_trial_bar_dismissed_until';

/**
 * Slim persistent bar shown at the very top of the page while the user is on
 * a Growth trial. Auto-hides when the trial ends or when the user upgrades.
 */
const TrialBar = () => {
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const { pathname } = useLocation();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) { setSub(null); return; }
    // Skip on auth / public routes
    if (['/login', '/register', '/forgot-password', '/reset-password'].some((p) => pathname.startsWith(p))) return;
    saasMySubscription().then(setSub).catch(() => setSub(null));
  }, [user, pathname]);

  useEffect(() => {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until > Date.now()) setDismissed(true);
  }, [pathname]);

  // Publish the bar height as a CSS variable so the Navbar can offset itself
  useEffect(() => {
    const visible = !!sub && !dismissed && sub.status === 'trialing' && sub.renewal_at;
    document.documentElement.style.setProperty('--trial-bar-h', visible ? '36px' : '0px');
    return () => document.documentElement.style.setProperty('--trial-bar-h', '0px');
  }, [sub, dismissed]);

  if (!sub || dismissed) return null;
  if (sub.status !== 'trialing' || !sub.renewal_at) return null;

  const msLeft = new Date(sub.renewal_at) - new Date();
  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
  if (daysLeft <= 0) return null;

  const dismiss = () => {
    // Hide for 24h; re-appears next day
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 86400000));
    setDismissed(true);
  };

  const urgent = daysLeft <= 3;
  const bg = urgent ? 'bg-amber-500' : 'bg-[#2563eb]';
  const label = lang === 'ar'
    ? (urgent ? `⏰ يتبقى ${daysLeft} يوم${daysLeft === 1 ? '' : 'اً'} من تجربتك المجانية — رقّي الآن قبل انتهائها`
              : `🎉 أنت في تجربة Growth المجانية — يتبقى ${daysLeft} يوم${daysLeft === 1 ? '' : 'اً'}`)
    : (urgent ? `⏰ Only ${daysLeft} day${daysLeft === 1 ? '' : 's'} left on your free trial — upgrade before it ends`
              : `🎉 You're on the free Growth trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`);

  return (
    <div dir={dir} className={`${bg} text-white text-xs sm:text-sm fixed top-0 inset-x-0 z-[60] shadow-md`} data-testid="trial-bar" style={{ height: '36px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-full flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        <div className="flex-1 truncate font-medium">{label}</div>
        <Link to="/accounting/pricing" className="hidden sm:inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 backdrop-blur rounded-md px-3 py-1 text-[11px] font-bold uppercase tracking-widest transition-all" data-testid="trial-bar-upgrade">
          {lang === 'ar' ? 'رقّي الآن' : 'Upgrade'} <ArrowRight className="w-3 h-3 rtl:rotate-180" />
        </Link>
        <button onClick={dismiss} aria-label="Dismiss" className="opacity-70 hover:opacity-100 ms-1" data-testid="trial-bar-dismiss">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default TrialBar;
